import jsPDF from "jspdf";
import "jspdf-autotable";
import { PlusJakartaSansFontBase64String } from "../../Helpers/PlusJakartaSans-base64String";
import { imgData } from "../../Helpers/smestLogoBase64ImgData";

declare module "jspdf" {
  interface jsPDF {
    getImageProperties?: (imageData: string | HTMLImageElement) => {
      width: number;
      height: number;
    };
  }
}

export interface BondInfo {
  isin?: string;
  securityType?: { type?: string };
}
export interface CalculatedValues {}
export interface UserDetails {
  firstName?: string;
  lastName?: string;
}

export interface IAnalysisReport {
  aboutTheIssuer: string;
  interestPaymentHistory: number;
  interestPaymentHistoryText: string;
  useOfTheIssue: string;
  managementRows: ManagementRow[];
  useOfTheIssueHeading: string;
  cons: Con[];
  pros: Con[];
  quarterlyRevenues: QuarterlyRevenue[];
  shareholdingPatterns: ShareholdingPattern[];
  lookoutOnFinancials: string;
  issuer_master_uuid: string;
  categoryWiseSHPRecords: CategoryWiseSHPRecord[];
  aboutTheIssuerHeading: string;
  interestPaymentHistoryHeading: string;
  managementHeading: string;
  ratingDriversHeading: string;
  quarterlyRevenueHeading: string;
  categoryWiseSHPHeading: string;
  shareholdingPatternHeading: string;
  outlookonFinancialsHeading: string;
}

export interface CategoryWiseSHPRecord {
  id: number;
  categoryName: string;
  particular: string;
  shareholding: string;
}
export interface Con {
  serial: number;
  heading: string;
  text: string;
}
export interface ManagementRow {
  name: string;
  information: string;
  avatar: string;
  index: number;
}
export interface QuarterlyRevenue {
  halfYear: string;
  revenue: string;
  profitAfterTax: string;
  serial: number;
}
export interface ShareholdingPattern {
  tag: string;
  value: string;
  serial: number;
}
export interface INews {
  uuid: string;
  title: string;
  body: string;
  publishedAt: Date;
  image: null;
  issuerMaster: string;
  insertedAt: Date;
  updatedAt: Date;
}

type Options = { startY?: number };

// --------- Helpers ---------
export function is_gsec_sdl_tBill(securityType: string) {
  return (
    securityType === "State Development Loans" ||
    securityType === "Government Securities" ||
    securityType === "T Bill" ||
    securityType === "Treasury Bill"
  );
}

export const parseLookoutOnFinancialsHTMLString = (html: string) => {
  const resultArray: { heading: string; content: string }[] = [];
  const regex = /<h1><strong>(.*?)<\/strong><\/h1>\s*<p>(.*?)<\/p>/gs;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const headingContent = match[1]?.trim() ?? "";
    const content = match[2]?.trim() ?? "";
    resultArray.push({ heading: headingContent, content });
  }
  return resultArray;
};

const normalize = (t?: any) => {
  try {
    if (t == null) return "";
    if (Array.isArray(t)) t = t.join(" ");
    const s = String(t);
    return s
      .replace(/&nbsp;/g, " ")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

// Allow wrapping inside very long tokens by inserting zero-width spaces
const softenLongTokens = (t: string, limit = 28) => {
  return t
    .split(" ")
    .map((tok) => {
      if (tok.length <= limit) return tok;
      const parts: string[] = [];
      for (let i = 0; i < tok.length; i += limit)
        parts.push(tok.slice(i, i + limit));
      return parts.join("\u200b");
    })
    .join(" ");
};

const safeText = (t?: string) => softenLongTokens(normalize(t || "-"));

const setFontIfAny = (doc: jsPDF) => {
  if (PlusJakartaSansFontBase64String) {
    doc.addFileToVFS(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      PlusJakartaSansFontBase64String
    );
    doc.addFont(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      "PlusJakartaSans",
      "normal"
    );
    doc.addFont(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      "PlusJakartaSans",
      "bold"
    );
    doc.setFont("PlusJakartaSans", "normal");
  }
};

const drawCompanyLogoTopRight = (doc: jsPDF, M: number) => {
  const W = doc.internal.pageSize.getWidth();
  try {
    if (imgData) {
      const dataUrl = imgData.startsWith("data:")
        ? imgData
        : `data:image/png;base64,${imgData}`;
      const maxW = 140,
        maxH = 48;
      let drawW = 120,
        drawH = 40;
      if (typeof (doc as any).getImageProperties === "function") {
        const p = (doc as any).getImageProperties!(dataUrl);
        const scale = Math.min(
          maxW / (p.width || maxW),
          maxH / (p.height || maxH)
        );
        drawW = (p.width || maxW) * scale;
        drawH = (p.height || maxH) * scale;
      }
      doc.addImage(dataUrl, "PNG", W - M - drawW, M - 6, drawW, drawH);
    }
  } catch {}
};

const ensureSpace = (
  doc: jsPDF,
  y: number,
  needed: number,
  M: number,
  headerHeight: number
) => {
  const H = doc.internal.pageSize.getHeight();
  if (y + needed <= H - M) return y;
  // new page
  doc.addPage("a4");
  drawCompanyLogoTopRight(doc, M);
  return M + headerHeight + 8;
};

// Card padding constants (asymmetric to harden right padding)
const PAD_L = 16;
const PAD_R = 22; // right padding equals top padding for a hard right edge
const PAD_TOP = 22;
const PAD_BOTTOM = 8;
const GAP_LABEL_CONTENT = 8;
const LABEL_SIZE = 11; // weight emphasized via font weight, not size
const TEXT_SIZE = 10.25;
const LINE_H = TEXT_SIZE * 1.35;
const LABEL_WEIGHT = "bold";

// Outer card tint (distinct from page 1); inner management cards stay white
const drawCard = (doc: jsPDF, x: number, y: number, w: number, h: number) => {
  doc.setDrawColor(220);
  doc.roundedRect(x, y, w, h, 10, 10, "S");
};

// Section with heading + paragraph, safe wrapping and hardened right padding
const drawSection = (
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  heading: string,
  text?: string
) => {
  const txt = safeText(text);
  // Set body font BEFORE measuring so wrapping width is accurate
  doc.setFont(
    PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
    "normal"
  );
  doc.setFontSize(TEXT_SIZE);
  const lines = doc.splitTextToSize(txt, w - PAD_L - PAD_R);
  const labelH = 14;
  const totalH =
    PAD_TOP + labelH + GAP_LABEL_CONTENT + lines.length * LINE_H + PAD_BOTTOM;

  drawCard(doc, x, y, w, totalH);

  try {
    doc.setFont("helvetica", "bold");
  } catch {
    doc.setFont(undefined as any, LABEL_WEIGHT as any);
  }
  doc.setFontSize(LABEL_SIZE);
  doc.setTextColor(40);
  doc.text(heading, x + PAD_L, y + PAD_TOP);

  doc.setFont(
    PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
    "normal"
  );
  doc.setFontSize(TEXT_SIZE);
  doc.setTextColor(30);
  doc.text(lines, x + PAD_L, y + PAD_TOP + labelH + GAP_LABEL_CONTENT, {
    maxWidth: w - PAD_L - PAD_R,
  });
  return totalH;
};

// Paginated section (About the Issuer) with hardened right padding
const drawSectionPaginated = (
  doc: jsPDF,
  M: number,
  headerHeight: number,
  yStart: number,
  heading: string,
  rawText?: string
) => {
  const W = doc.internal.pageSize.getWidth();
  const fullW = W - 2 * M;
  const labelH = 14;

  let y = yStart;
  // Set body font BEFORE measuring so wrapping width is accurate
  doc.setFont(
    PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
    "normal"
  );
  doc.setFontSize(TEXT_SIZE);
  const words = doc.splitTextToSize(safeText(rawText), fullW - PAD_L - PAD_R);
  let idx = 0;
  let first = true;

  while (idx < words.length) {
    const H = doc.internal.pageSize.getHeight();
    const maxContentH =
      H - M - y - (PAD_TOP + labelH + GAP_LABEL_CONTENT + PAD_BOTTOM);
    const maxLines = Math.max(1, Math.floor(maxContentH / LINE_H));

    const chunk = words.slice(idx, idx + maxLines);
    const totalH =
      PAD_TOP + labelH + GAP_LABEL_CONTENT + chunk.length * LINE_H + PAD_BOTTOM;

    if (totalH > H - M - y) {
      y = ensureSpace(doc, y, totalH, M, headerHeight);
      continue;
    }

    drawCard(doc, M, y, fullW, totalH);

    try {
      doc.setFont("helvetica", "bold");
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT as any);
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(first ? heading : `${heading} (cont.)`, M + PAD_L, y + PAD_TOP);

    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    doc.setTextColor(30);
    doc.text(chunk, M + PAD_L, y + PAD_TOP + labelH + GAP_LABEL_CONTENT, {
      maxWidth: fullW - PAD_L - PAD_R,
    });

    y += totalH + 12;
    idx += chunk.length;
    first = false;
  }
  return y;
};

// ---- Management (main container + inner cards) ----
const measureInnerMgmtCard = (
  doc: jsPDF,
  innerW: number,
  name?: string,
  info?: string
) => {
  const SUB_PAD_L = 0;
  const SUB_PAD_R = 0;
  const SUB_PAD_TOP = 0; // no top padding
  const SUB_PAD_BOTTOM = 0;

  // Name styles
  const nameFirstSize = 10.5; // larger for actual name
  const nameSecondSize = 9.5; // smaller for designation
  const nameFirstLineH = nameFirstSize * 1.3;
  const nameSecondLineH = nameSecondSize * 1.3;

  // Info styles
  const infoSize = 9.75;
  const infoLineH = infoSize * 1.35;

  let nameFirstLines: string[] = [];
  let nameSecondLines: string[] = [];

  const wText = innerW - SUB_PAD_L - SUB_PAD_R;

  // Prepare name: split at first comma into two lines (name, designation)
  if (name && name.trim().length > 0) {
    const parts = name.split(",");
    const first = (parts[0] || "").trim();
    const rest = parts.slice(1).join(",").trim();

    try {
      doc.setFont("helvetica", "bolditalic");
    } catch {
      doc.setFont("helvetica", "bold");
    }
    // First line
    if (first) {
      doc.setFontSize(nameFirstSize);
      nameFirstLines = doc.splitTextToSize(first, wText) as string[];
    }
    // Second line (designation) in smaller font
    if (rest) {
      doc.setFontSize(nameSecondSize);
      nameSecondLines = doc.splitTextToSize(rest, wText) as string[];
    }
  }

  // Description lines (body)
  doc.setFont(
    PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
    "normal"
  );
  doc.setFontSize(infoSize);
  const lines = doc.splitTextToSize(safeText(info), wText) as string[];

  const nameBlockH =
    nameFirstLines.length * nameFirstLineH +
    (nameSecondLines.length > 0
      ? 2 + nameSecondLines.length * nameSecondLineH
      : 0);

  const h =
    SUB_PAD_TOP +
    (nameBlockH > 0 ? nameBlockH + 6 : 0) +
    lines.length * infoLineH +
    SUB_PAD_BOTTOM;

  return {
    h,
    lines,
    nameFirstLines,
    nameSecondLines,
    nameBlockH,
    SUB_PAD_L,
    SUB_PAD_R,
    SUB_PAD_TOP,
    SUB_PAD_BOTTOM,
    nameFirstSize,
    nameSecondSize,
    nameFirstLineH,
    nameSecondLineH,
    infoSize,
    infoLineH,
  };
};

const drawInnerMgmtCard = (
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  m: ReturnType<typeof measureInnerMgmtCard>,
  name?: string
) => {
  // No border or background for inner management items
  // Draw name block (two styles)
  let cursorY = y + m.SUB_PAD_TOP;
  if (
    (m.nameFirstLines && m.nameFirstLines.length) ||
    (m.nameSecondLines && m.nameSecondLines.length)
  ) {
    try {
      doc.setFont("helvetica", "bolditalic");
    } catch {
      doc.setFont("helvetica", "bold");
    }
    // First line (darker, larger)
    if (m.nameFirstLines && m.nameFirstLines.length) {
      doc.setFontSize(m.nameFirstSize);
      doc.setTextColor(40);
      doc.text(m.nameFirstLines, x + m.SUB_PAD_L, cursorY, {
        maxWidth: w - m.SUB_PAD_L - m.SUB_PAD_R,
      });
      cursorY += m.nameFirstLines.length * m.nameFirstLineH;
    }
    // Second line (lighter, smaller) — same weight
    if (m.nameSecondLines && m.nameSecondLines.length) {
      cursorY += 2; // small gap between first & second line groups
      doc.setFontSize(m.nameSecondSize);
      doc.setTextColor(120);
      doc.text(m.nameSecondLines, x + m.SUB_PAD_L, cursorY, {
        maxWidth: w - m.SUB_PAD_L - m.SUB_PAD_R,
      });
      cursorY += m.nameSecondLines.length * m.nameSecondLineH;
    }
  }

  // Description
  doc.setFont(
    PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
    "normal"
  );
  doc.setFontSize(m.infoSize);
  doc.setTextColor(30);
  const descY = y + m.SUB_PAD_TOP + (m.nameBlockH > 0 ? m.nameBlockH + 6 : 0);
  doc.text(m.lines, x + m.SUB_PAD_L, descY, {
    maxWidth: w - m.SUB_PAD_L - m.SUB_PAD_R,
  });
};

const drawManagementSection = (
  doc: jsPDF,
  M: number,
  headerHeight: number,
  yStart: number,
  rows: ManagementRow[]
) => {
  const W = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const fullW = W - 2 * M;
  const gridGap = 12;
  const colW = (fullW - gridGap) / 2;
  const labelH = 14;

  const topOfPage = M + headerHeight + 8;

  let y = yStart;
  let idx = 0;
  let iteration = 0;
  // True when y sits at the top of an otherwise empty page, i.e. there is no
  // more room to gain by breaking again — the row must be taken as-is.
  let onFreshPage = y <= topOfPage;

  while (idx < rows.length) {
    const title = iteration === 0 ? "Management" : "Management (cont.)";

    const available = pageH - M - y;
    const headerBlockH = PAD_TOP + labelH + GAP_LABEL_CONTENT;
    let contentH = 0;

    const measured: {
      left: ReturnType<typeof measureInnerMgmtCard>;
      right?: ReturnType<typeof measureInnerMgmtCard>;
      leftMeta: ManagementRow;
      rightMeta?: ManagementRow;
    }[] = [];

    while (idx < rows.length) {
      const leftMeta = rows[idx];
      const rightMeta = idx + 1 < rows.length ? rows[idx + 1] : undefined;
      const single = !rightMeta;
      // A row taller than a whole page must still be consumed, otherwise the
      // outer loop can never advance.
      const mustFit = onFreshPage && measured.length === 0;

      if (single) {
        const mLeft = measureInnerMgmtCard(
          doc,
          fullW - PAD_L - PAD_R,
          leftMeta?.name,
          leftMeta?.information
        );
        const rowH = mLeft.h;
        if (
          headerBlockH + contentH + rowH + PAD_BOTTOM > available &&
          !mustFit
        ) {
          break;
        }
        measured.push({ left: mLeft, leftMeta });
        contentH += rowH + 12;
        idx += 1;
      } else {
        const mLeft = measureInnerMgmtCard(
          doc,
          colW - PAD_L - PAD_R,
          leftMeta?.name,
          leftMeta?.information
        );
        const mRight = measureInnerMgmtCard(
          doc,
          colW - PAD_L - PAD_R,
          rightMeta?.name,
          rightMeta?.information
        );
        const rowH = Math.max(mLeft.h, mRight.h);
        if (headerBlockH + contentH + rowH + PAD_BOTTOM > available) {
          if (!mustFit) break;
          // The pair is too tall even for an empty page — lay the first entry
          // out full width (roughly half the height) and start a new container
          // for the rest.
          const mWide = measureInnerMgmtCard(
            doc,
            fullW - PAD_L - PAD_R,
            leftMeta?.name,
            leftMeta?.information
          );
          measured.push({ left: mWide, leftMeta });
          contentH += mWide.h + 12;
          idx += 1;
          break;
        }
        measured.push({ left: mLeft, right: mRight, leftMeta, rightMeta });
        contentH += rowH + 12;
        idx += 2;
      }
    }

    if (measured.length === 0) {
      // Nothing fit in the space left on this page — always move to a new one.
      // (ensureSpace() only breaks when its own `needed` does not fit, which is
      // why it used to return the same y here and spin forever.)
      doc.addPage("a4");
      drawCompanyLogoTopRight(doc, M);
      y = topOfPage;
      onFreshPage = true;
      continue;
    }

    const containerH = headerBlockH + contentH + PAD_BOTTOM;

    // Outer management container
    drawCard(doc, M, y, fullW, containerH);
    try {
      doc.setFont("helvetica", "bold");
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT as any);
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(title, M + PAD_L, y + PAD_TOP);

    // Inner grid origin
    let cy = y + PAD_TOP + labelH + GAP_LABEL_CONTENT;
    const innerXLeft = M + PAD_L;
    const innerXRight = M + colW + gridGap + PAD_L;
    const innerWLeft = colW - PAD_L - PAD_R;
    const innerWRight = innerWLeft;
    const innerWFull = fullW - PAD_L - PAD_R;

    let startMetaIdx = iteration === 0 ? 0 : undefined;

    for (const row of measured) {
      if (row.right) {
        const rowH = Math.max(row.left.h, row.right.h);
        drawInnerMgmtCard(
          doc,
          innerXLeft,
          cy,
          innerWLeft,
          row.left,
          row.leftMeta?.name
        );
        drawInnerMgmtCard(
          doc,
          innerXRight,
          cy,
          innerWRight,
          row.right,
          row.rightMeta?.name
        );
        cy += rowH + 12;
      } else {
        drawInnerMgmtCard(
          doc,
          innerXLeft,
          cy,
          innerWFull,
          row.left,
          row.leftMeta?.name
        );
        cy += row.left.h + 12;
      }
    }

    y += containerH + 12;
    iteration++;
    onFreshPage = false;

    if (idx < rows.length) {
      y = ensureSpace(
        doc,
        y,
        PAD_TOP + labelH + GAP_LABEL_CONTENT + 60,
        M,
        headerHeight
      );
      onFreshPage = y <= topOfPage;
    }
  }

  return y;
};

// --------- Main ---------
export async function generateBondQuotationThirdPage(
  doc: jsPDF,
  bond: BondInfo,
  calculated: CalculatedValues,
  userDetails: UserDetails,
  report: IAnalysisReport,
  news: INews[],
  opts: Options = {}
): Promise<{ doc: jsPDF; nextY: number }> {
  const M = (doc as any).__margin ?? 36;
  const headerHeight = (doc as any).__headerHeight ?? 42;
  const W = doc.internal.pageSize.getWidth();

  setFontIfAny(doc);

  // Start right after the second-page disclaimer
  let y =
    typeof opts.startY === "number"
      ? opts.startY
      : (doc as any).__nextY ?? M + headerHeight + 12;
  if (y < M + headerHeight + 8) y = M + headerHeight + 8;

  // 1) About + Use of the Issue — two-column row
  {
    const fullW = W - 2 * M;
    const gapX = 12;
    const colW = (fullW - gapX) / 2;

    const aboutHeading = report?.aboutTheIssuerHeading || "About the Issuer";
    const aboutText = safeText(report?.aboutTheIssuer || "-");
    const useHeading = report?.useOfTheIssueHeading || "Use of the Issue";
    const useText = safeText(report?.useOfTheIssue || "-");

    // Ensure font set before measuring
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    const aboutLines = doc.splitTextToSize(aboutText, colW - PAD_L - PAD_R);
    const useLines = doc.splitTextToSize(useText, colW - PAD_L - PAD_R);

    const labelH = 14;
    const aboutH =
      PAD_TOP +
      labelH +
      GAP_LABEL_CONTENT +
      aboutLines.length * LINE_H +
      PAD_BOTTOM;
    const useH =
      PAD_TOP +
      labelH +
      GAP_LABEL_CONTENT +
      useLines.length * LINE_H +
      PAD_BOTTOM;
    const rowH = Math.max(aboutH, useH);

    y = ensureSpace(doc, y, rowH, M, headerHeight);

    // Left: About
    drawCard(doc, M, y, colW, rowH);
    try {
      doc.setFont("helvetica", LABEL_WEIGHT as any);
    } catch {
      doc.setFont("helvetica", "bold");
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(aboutHeading, M + PAD_L, y + PAD_TOP);
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    doc.setTextColor(30);
    doc.text(aboutLines, M + PAD_L, y + PAD_TOP + labelH + GAP_LABEL_CONTENT, {
      maxWidth: colW - PAD_L - PAD_R,
    });

    // Right: Use of the Issue
    const rightX = M + colW + gapX;
    drawCard(doc, rightX, y, colW, rowH);
    try {
      doc.setFont("helvetica", LABEL_WEIGHT as any);
    } catch {
      doc.setFont("helvetica", "bold");
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(useHeading, rightX + PAD_L, y + PAD_TOP);
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    doc.setTextColor(30);
    doc.text(
      useLines,
      rightX + PAD_L,
      y + PAD_TOP + labelH + GAP_LABEL_CONTENT,
      { maxWidth: colW - PAD_L - PAD_R }
    );

    y += rowH + 12;
  }

  // 2) Management — inside a main card with inner white cards, responsive grid
  if (
    Array.isArray(report?.managementRows) &&
    report.managementRows.length > 0
  ) {
    y = drawManagementSection(doc, M, headerHeight, y, report.managementRows);
  }

  // 3) Key Metrics — three-column row (Debt/EBITDA, Interest coverage ratio, Liquidity)
  {
    const fullW = W - 2 * M;
    const gapX = 12;
    const colW = (fullW - 2 * gapX) / 3;

    const parsed = parseLookoutOnFinancialsHTMLString(
      report?.lookoutOnFinancials || ""
    );
    const leftHeading = is_gsec_sdl_tBill(bond?.securityType?.type || "")
      ? "GOVERNMENT-BACKED"
      : "Debt/EBITDA";
    const midHeading = is_gsec_sdl_tBill(bond?.securityType?.type || "")
      ? "STABLE RETURNS"
      : "Interest coverage ratio";
    const rightHeading = "Liquidity";

    const leftText = safeText(parsed?.[1]?.content || "-");
    const midText = safeText(parsed?.[2]?.content || "-");
    const rightText = safeText(parsed?.[0]?.content || "-");

    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    const leftLines = doc.splitTextToSize(leftText, colW - PAD_L - PAD_R);
    const midLines = doc.splitTextToSize(midText, colW - PAD_L - PAD_R);
    const rightLines = doc.splitTextToSize(rightText, colW - PAD_L - PAD_R);

    const labelH = 14;
    const leftH =
      PAD_TOP +
      labelH +
      GAP_LABEL_CONTENT +
      leftLines.length * LINE_H +
      PAD_BOTTOM;
    const midH =
      PAD_TOP +
      labelH +
      GAP_LABEL_CONTENT +
      midLines.length * LINE_H +
      PAD_BOTTOM;
    const rightH =
      PAD_TOP +
      labelH +
      GAP_LABEL_CONTENT +
      rightLines.length * LINE_H +
      PAD_BOTTOM;
    const rowH = Math.max(leftH, midH, rightH);

    y = ensureSpace(doc, y, rowH, M, headerHeight);

    // Left card
    drawCard(doc, M, y, colW, rowH);
    try {
      doc.setFont("helvetica", LABEL_WEIGHT as any);
    } catch {
      doc.setFont("helvetica", "bold");
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(leftHeading, M + PAD_L, y + PAD_TOP);
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    doc.setTextColor(30);
    doc.text(leftLines, M + PAD_L, y + PAD_TOP + labelH + GAP_LABEL_CONTENT, {
      maxWidth: colW - PAD_L - PAD_R,
    });

    // Mid card
    const midX = M + colW + gapX;
    drawCard(doc, midX, y, colW, rowH);
    try {
      doc.setFont("helvetica", LABEL_WEIGHT as any);
    } catch {
      doc.setFont("helvetica", "bold");
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(midHeading, midX + PAD_L, y + PAD_TOP);
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    doc.setTextColor(30);
    doc.text(midLines, midX + PAD_L, y + PAD_TOP + labelH + GAP_LABEL_CONTENT, {
      maxWidth: colW - PAD_L - PAD_R,
    });

    // Right card (Liquidity)
    const rightX = M + (colW + gapX) * 2;
    drawCard(doc, rightX, y, colW, rowH);
    try {
      doc.setFont("helvetica", LABEL_WEIGHT as any);
    } catch {
      doc.setFont("helvetica", "bold");
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(rightHeading, rightX + PAD_L, y + PAD_TOP);
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    doc.setFontSize(TEXT_SIZE);
    doc.setTextColor(30);
    doc.text(
      rightLines,
      rightX + PAD_L,
      y + PAD_TOP + labelH + GAP_LABEL_CONTENT,
      { maxWidth: colW - PAD_L - PAD_R }
    );

    y += rowH + 12;
  }
  (doc as any).__nextY = y;
  return { doc, nextY: y };
}
